import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewEncapsulation,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import dayjs from "dayjs";

import { FacebookPostPreviewType } from "../../../../shared/components/post-mockups/facebook-post-mock-up/facebook-post-mock-up.component";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCalendar } from "../../../../shared/models/brandCampaignCalendar";
import {
  BrandCalendarPost,
  BrandCampaignCalendarPost,
  BrandCampaignFacebookCalendarPost,
} from "../../../../shared/models/brandCampaignCalendarPost";
import { AdActionType } from "../../../../shared/enums/campaign.enums";
import {
  HOURS_24,
  MINUTES,
  TIMEZONES,
} from "../../../../shared/services/calendar.data";
import { DialogService } from "../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandUrl } from "../../../brand.url";
import {
  BrandCalendarPostService,
  ICalendarPostAPIParameters,
} from "../../shared/services/brand-calendar-post.service";

import { BrandCampaignContentCalendarPostEditDialogData } from "./brand-campaign-content-calendar-post-edit-dialog-data";
import { isHtmlElement } from "../../../../shared/utils/assert";

@Component({
  selector: "app-brand-campaign-content-calendar-post-edit-dialog",
  templateUrl:
    "./brand-campaign-content-calendar-post-edit-dialog.component.html",
  styleUrl: "./brand-campaign-content-calendar-post-edit-dialog.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class BrandCampaignContentCalendarPostEditDialogComponent
  implements OnInit
{
  @Output()
  public readonly postEdited = new EventEmitter<BrandCampaignCalendarPost>();
  @Output()
  public readonly postRemoved = new EventEmitter<BrandCampaignCalendarPost>();

  protected readonly FacebookPreviewType = FacebookPostPreviewType;
  protected readonly hours = HOURS_24;
  protected readonly minutes = MINUTES;
  protected readonly timezones = Object.keys(TIMEZONES);
  protected campaign: BrandCampaign;
  protected facebookPreviewType = FacebookPostPreviewType.Facebook;
  protected isDarkPost: boolean;
  protected numberOfAdsCreated = 0;
  protected post: BrandCalendarPost;
  protected scheduledPublishDateHour: string;
  protected scheduledPublishDateMinute: string;
  protected scheduledPublishDateTimezone: string;
  protected willBecomeAd: boolean;

  private readonly brandId: number;
  private campaignCalendar: BrandCampaignCalendar;
  private selectedPublishDate: Date;

  constructor(
    private readonly brandCalendarPostService: BrandCalendarPostService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA)
    data: BrandCampaignContentCalendarPostEditDialogData,
    private readonly router: Router,
    private readonly dialogService: DialogService,
  ) {
    this.post = data.post;
    this.isDarkPost = this.post.isDarkPost;
    this.willBecomeAd = this.post.willBecomeAd;
    this.brandId = data.brandId;
    this.campaign = data.campaign;
    this.campaignCalendar = data.campaignCalendar;
    this.selectedPublishDate = this.post.suggestedPublishDate;
    this.scheduledPublishDateHour = dayjs(this.selectedPublishDate).format(
      "HH",
    );
    this.scheduledPublishDateMinute = dayjs(this.selectedPublishDate).format(
      "mm",
    );
    this.scheduledPublishDateTimezone = this.post.suggestedPublishDateTimezone;

    if (this.post instanceof BrandCampaignFacebookCalendarPost) {
      this.facebookPreviewType = this.post.postToFacebook
        ? FacebookPostPreviewType.Facebook
        : FacebookPostPreviewType.Instagram;
    }
  }

  public ngOnInit(): void {
    setTimeout(() => this.calculateNumberOfAdsCreated(), 500);
  }

  protected checkLink(event: MouseEvent): void {
    if (
      isHtmlElement(event.target) &&
      event.target.classList.contains("can-not-save-post-warning--link")
    ) {
      this.goToGoogleAds();
    }
  }

  protected closeDialog(): void {
    this.dialog.closeAll();
  }

  protected onSave(): void {
    const newDate = this.selectedPublishDate;
    newDate.setHours(+this.scheduledPublishDateHour);
    newDate.setMinutes(+this.scheduledPublishDateMinute);

    const apiParams: ICalendarPostAPIParameters = {
      post_id: this.post.id,
      suggested_publish_date: dayjs(newDate).unix(),
      will_become_ad: this.willBecomeAd,
      is_dark_post: this.isDarkPost,
      scheduled_publish_date_timezone: this.scheduledPublishDateTimezone,
    };

    this.brandCalendarPostService
      .updateBrandCalendarPost(
        this.campaign.id,
        this.campaignCalendar.id,
        this.post.calendarPostId,
        apiParams,
      )
      .subscribe({
        next: (calendarPost) => {
          this.postEdited.emit(calendarPost);
          this.calculateNumberOfAdsCreated();
          this.closeDialog();
        },
        error: () => {
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  protected calculateNumberOfAdsCreated(): void {
    this.numberOfAdsCreated = 0;

    if (!this.campaign || !this.campaign.currentCalendar) {
      return;
    }

    this.campaign.currentCalendar.calendarPosts.forEach((post) => {
      if (post.isGoogleAd) {
        this.numberOfAdsCreated++;
      } else if (this.post.calendarPostId === post.calendarPostId) {
        if (this.willBecomeAd) {
          this.numberOfAdsCreated++;
        }
      } else if (post.willBecomeAd) {
        this.numberOfAdsCreated++;
      }
    });
  }

  protected deletePostDialog(): void {
    this.dialogService.confirm({
      title: "brand.campaign.contentCalendar.confirmDeletePostTitle",
      message: "brand.campaign.contentCalendar.confirmDeletePostDescription",
      onConfirm: () => this.deletePost(),
    });
  }

  private deletePost(): void {
    this.brandCalendarPostService
      .deleteBrandCalendarPost(
        this.campaign.id,
        this.campaignCalendar.id,
        this.post.calendarPostId,
      )
      .subscribe({
        next: () => {
          this.closeDialog();
          this.postRemoved.emit(this.post);
          this.calculateNumberOfAdsCreated();
        },
        error: () => {
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  protected showCanNotCreateGoogleAdWarning(): boolean {
    return !this.campaign.useGoogleAds && this.post.isGoogleAd;
  }

  private goToGoogleAds(): void {
    this.dialog.closeAll();
    this.router.navigateByUrl(
      BrandUrl.Campaign.Ads(this.brandId, this.campaign.id),
    );
  }

  protected isDarkPostDisabled(): boolean {
    if (!this.campaign) {
      return true;
    }
    if (this.campaign.adActionType === AdActionType.Boost) {
      return true;
    }
    return !this.campaign.isEditable || !this.willBecomeAd;
  }

  protected toggleFacebookPreviewType(): void {
    this.facebookPreviewType =
      this.facebookPreviewType === FacebookPostPreviewType.Facebook
        ? FacebookPostPreviewType.Instagram
        : FacebookPostPreviewType.Facebook;
  }

  protected showFacebookPreview(): boolean {
    return this.facebookPreviewType === this.FacebookPreviewType.Facebook;
  }

  protected showInstagramPreview(): boolean {
    if (this.showFacebookPreview()) {
      return false;
    }
    return this.facebookPreviewType === this.FacebookPreviewType.Instagram;
  }

  protected showMediaPreviewButton(): boolean {
    if (this.post instanceof BrandCampaignFacebookCalendarPost) {
      if (this.post.willBecomeAd) {
        return true;
      }
      return this.post.postToFacebook && this.post.postToInstagram;
    }

    return false;
  }
}
